import React from 'react';

type Props = {
  open: boolean;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onLogin: () => void;
  onEmailLinkLogin: (email: string) => Promise<void>;
  showClose?: boolean;
};

const LoginModal: React.FC<Props> = ({
  open,
  loading,
  error,
  onClose,
  onLogin,
  onEmailLinkLogin,
  showClose = true,
}) => {
  const [email, setEmail] = React.useState('');
  const [emailSending, setEmailSending] = React.useState(false);
  const [sentEmail, setSentEmail] = React.useState<string | null>(null);

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const targetEmail = email.trim();
    if (!targetEmail) return;

    setEmailSending(true);
    setSentEmail(null);
    try {
      await onEmailLinkLogin(targetEmail);
      setSentEmail(targetEmail);
    } catch {
      setSentEmail(null);
    } finally {
      setEmailSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-600">NeedRadar</p>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">ログイン</h2>
          </div>
          {showClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="閉じる"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>

        <p className="mt-3 text-sm leading-6 text-gray-600">
          検索履歴・保存キーワード・AI分析を、自分の調査資産として残せます。
        </p>

        <div className="mt-5 grid grid-cols-1 gap-2 text-sm text-gray-700">
          <div className="flex items-center gap-2 rounded bg-blue-50 px-3 py-2">
            <i className="fa-solid fa-clock-rotate-left text-blue-500"></i>
            <span>検索履歴からすぐ再分析</span>
          </div>
          <div className="flex items-center gap-2 rounded bg-amber-50 px-3 py-2">
            <i className="fa-solid fa-star text-amber-500"></i>
            <span>気になるキーワードを保存</span>
          </div>
          <div className="flex items-center gap-2 rounded bg-emerald-50 px-3 py-2">
            <i className="fa-solid fa-database text-emerald-500"></i>
            <span>AI分析や取得済みデータを活用</span>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onLogin}
          disabled={loading}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded bg-blue-500 px-4 py-3 text-sm font-bold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="fa-brands fa-google"></i>
          {loading ? '確認中...' : 'Googleでログイン'}
        </button>

        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200"></div>
          <span className="text-xs font-semibold text-gray-400">または</span>
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-3">
          <label className="block text-xs font-semibold text-gray-600" htmlFor="email-link-login">
            メールリンクでログイン
          </label>
          <div className="flex gap-2">
            <input
              id="email-link-login"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="min-w-0 flex-1 rounded border border-gray-200 px-3 py-3 text-sm text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
            <button
              type="submit"
              disabled={emailSending || loading || !email.trim()}
              className="shrink-0 rounded border border-blue-200 bg-blue-50 px-3 py-3 text-sm font-bold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {emailSending ? '送信中...' : '送信'}
            </button>
          </div>
          {sentEmail && (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-800">
              {sentEmail} にログインリンクを送信しました。メール内のリンクを開くとログインできます。
            </div>
          )}
        </form>

        <p className="mt-3 text-xs leading-5 text-gray-400">
          ログインすると、
          <a href="/terms" className="font-semibold text-gray-500 underline underline-offset-2">
            利用規約
          </a>
          ・
          <a href="/privacy" className="font-semibold text-gray-500 underline underline-offset-2">
            プライバシーポリシー
          </a>
          に同意したものとします。
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
